/** Shared MyWellfie Privacy Policy copy — user app + adhoc booth. */

export const PRIVACY_POLICY_LAST_UPDATED = 'September 8, 2026';

export const PRIVACY_CONTACT_EMAIL = 'support@mywellfie.com';

export type PrivacySection = {
  id: string;
  title: string;
  paragraphs?: string[];
  bullets?: string[];
};

export const PRIVACY_POLICY_SECTIONS: PrivacySection[] = [
  {
    id: 'intro',
    title: 'Introduction',
    paragraphs: [
      'MyWellfie ("we", "us", "our") provides camera-based wellness scanning and related services through our website and applications. This Privacy Policy explains how we collect, use, store, and share information when you use MyWellfie.',
      'This policy applies whether you use MyWellfie as a registered user (mywellfie.ai/app) or as a guest at a booth, kiosk, or event scan (display / adhoc experience).',
    ],
  },
  {
    id: 'collect',
    title: 'Information we collect',
    paragraphs: ['Depending on how you use MyWellfie, we may collect:'],
    bullets: [
      'Account information: email address, name, and password (for email sign-up) or profile information from Google Sign-In (name, email, profile photo, provider identifier).',
      'Health and profile information you provide: sex, date of birth or age, height, weight, smoking status, diet, and chronic conditions — used to personalize scan estimates and reports.',
      'Scan and wellness data: physiological estimates derived from a short face video (for example blood pressure trends, pulse, HRV, wellness scores) and related report content.',
      'Booth / guest session data: session identifiers, optional email for pack recovery or receipts, payment confirmation references, and profile answers tied to a single scan flow.',
      'Payment information: subscriptions and scan packs are processed by third-party payment providers (such as Stripe or HitPay). We receive payment status and billing references, not your full card or bank details.',
      'Technical data: device/browser type, IP address, timestamps, and diagnostic logs used for security and service reliability.',
    ],
  },
  {
    id: 'camera',
    title: 'Camera and video data',
    paragraphs: [
      'MyWellfie uses your device camera to analyze subtle visual signals during a short resting scan. Processing is performed to generate wellness estimates and reports.',
      'Facial video is processed for scan analysis. We do not require long-term storage of raw video after processing unless you choose to save scan history to your account. Retention of scan results and reports may vary based on your account settings and product features.',
    ],
  },
  {
    id: 'use',
    title: 'How we use information',
    bullets: [
      'Create and manage your account and scan entitlements.',
      'Run scans, generate reports, and show trends over time.',
      'Process payments and manage subscriptions or scan credits.',
      'Send service-related communications (such as pack links or account recovery).',
      'Maintain security, prevent fraud, and improve our services.',
      'Comply with legal obligations.',
    ],
  },
  {
    id: 'sharing',
    title: 'How we share information',
    paragraphs: [
      'We do not sell your personal information. We share data only with service providers that help us operate MyWellfie, under appropriate contractual safeguards, including:',
      'We may also disclose information if required by law or to protect the rights, safety, and security of MyWellfie and our users.',
    ],
    bullets: [
      'Identity providers (Google) when you choose Sign in with Google.',
      'Payment processors (Stripe, HitPay) when you purchase scans or subscriptions.',
      'Cloud hosting and infrastructure providers that store and run our applications.',
      'Scan processing technology partners used to derive wellness metrics from camera input.',
    ],
  },
  {
    id: 'retention',
    title: 'Data retention',
    paragraphs: [
      'We retain account and scan history for as long as your account is active or as needed to provide the service. Guest booth session data is retained for a limited period needed to complete the scan, deliver reports, and reconcile payments.',
      'You may request deletion of your account and associated data by contacting us at the email below.',
    ],
  },
  {
    id: 'rights',
    title: 'Your choices and rights',
    bullets: [
      'Access and update profile information in the app.',
      'Use email/password or Google sign-in according to your account setup.',
      'Request access, correction, or deletion of your personal data by emailing us.',
      'Where applicable under local law, you may have additional privacy rights (for example GDPR or PDPA).',
    ],
  },
  {
    id: 'security',
    title: 'Security',
    paragraphs: [
      'We use reasonable technical and organizational measures to protect information, including encrypted connections (HTTPS) for data in transit. No method of transmission or storage is completely secure.',
    ],
  },
  {
    id: 'children',
    title: 'Children',
    paragraphs: [
      'MyWellfie is not intended for children under 13 (or the minimum age required in your jurisdiction). We do not knowingly collect personal information from children.',
    ],
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time. The "Last updated" date at the top will reflect the latest version. Continued use of MyWellfie after changes means you accept the updated policy.',
    ],
  },
  {
    id: 'contact',
    title: 'Contact us',
    paragraphs: [
      `Questions about this Privacy Policy or your data? Email ${PRIVACY_CONTACT_EMAIL}.`,
    ],
  },
];

/** Public URL for the privacy policy page (works from /app and /dev/display). */
export function getPrivacyPolicyUrl(): string {
  if (typeof window === 'undefined') {
    return 'https://mywellfie.ai/app/privacy';
  }
  const { origin, pathname } = window.location;
  if (pathname.includes('/dev/')) {
    return `${origin}/dev/app/privacy`;
  }
  return `${origin}/app/privacy`;
}
